@if (batchProgress.isBatching) {
  <section class="step-progress" role="status">
    <progress-circle [progress]="batchProgress.percentage" label="Check progress" />
    <p class="step-progress__title">
      @if (batchProgress.totalBatches > 0) {
        Checking batch {{ batchProgress.currentBatch }} of {{ batchProgress.totalBatches }}
      } @else {
        Getting the check ready
      }
    </p>
    <p class="step-progress__hint">This file is large, so it is checked in batches. Keep this page open.</p>
  </section>
} @else {
  <section class="cu">
    <!-- Where the import stands -->
    <div class="step-stats">
      <div class="step-stat">
        <span class="step-stat__value">{{ formatCount(totalCount) }}</span>
        <span class="step-stat__label">Rows in this import</span>
      </div>
      <div class="step-stat">
        <span class="step-stat__value">{{ formatCount(readyCount) }}</span>
        <span class="step-stat__label"><span class="step-dot" data-tone="ok"></span>Ready</span>
      </div>
      <div class="step-stat" [attr.data-tone]="invalidCount ? 'bad' : null">
        <span class="step-stat__value">{{ formatCount(invalidCount) }}</span>
        <span class="step-stat__label">
          <span class="step-dot" [attr.data-tone]="invalidCount ? 'bad' : 'idle'"></span>
          Need fixing
          @if (duplicateCount) {
            · {{ formatCount(duplicateCount) }} repeated {{ duplicateCount === 1 ? 'number' : 'numbers' }}
          }
        </span>
      </div>
    </div>

    <!-- Bulk fixes -->
    @if (invalidCount) {
      <div class="cu__fixes">
        @if (missingRegionCount) {
          <div class="step-notice">
            <span class="bk-sym" aria-hidden="true">public</span>
            <p class="step-notice__text">
              {{ formatCount(missingRegionCount) }} {{ missingRegionCount === 1 ? 'number has' : 'numbers have' }} no
              country code. Choose the country they are from.
            </p>
            <div class="step-notice__actions">
              <label class="bk-sr-only" for="cu-country">Country for numbers without a code</label>
              <select
                id="cu-country"
                class="bk-field__input cu__country"
                [value]="defaultCountry"
                (change)="defaultCountry = $any($event.target).value"
              >
                <option value="">Choose a country</option>
                @for (country of countries; track country) {
                  <option [value]="country">{{ country }}</option>
                }
              </select>
              <button type="button" class="bk-btn bk-btn--ghost" [disabled]="!defaultCountry" (click)="applyDefaultCountry()">
                Apply
              </button>
            </div>
          </div>
        }

        <div class="step-notice">
          <span class="bk-sym" aria-hidden="true">build</span>
          <p class="step-notice__text">
            Fix a row with its edit button, or take the rows out and fix them in your spreadsheet.
          </p>
          <div class="step-notice__actions">
            <button type="button" class="bk-btn bk-btn--ghost" (click)="downloadRowsNeedingFixes()">
              <span class="bk-sym" aria-hidden="true">download</span>
              Download rows to fix
            </button>
            <button type="button" class="bk-btn bk-btn--ghost" (click)="openRemoveRowsPrompt()">
              Remove {{ formatCount(invalidCount) }} {{ invalidCount === 1 ? 'row' : 'rows' }}
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Always present, so screen readers hear each new result of a bulk fix. -->
    <p class="bk-sr-only" role="status">{{ fixNotice }}</p>
    @if (fixNotice) {
      <p class="cu__done">
        <span class="bk-sym" aria-hidden="true">check</span>
        {{ fixNotice }}
      </p>
    }

    @if (checkError) {
      <div class="step-notice is-error" role="alert">
        <span class="bk-sym" aria-hidden="true">error</span>
        <p class="step-notice__text">{{ checkError }}</p>
      </div>
    }

    <csv-cleanup-table (rowsChanged)="onRowsChanged()" />
  </section>

  <stepper-controls
    class="sticky bottom-0"
    leftButtonLabel="Back"
    rightButtonLabel="Continue"
    [rightButtonDisabled]="!totalCount"
    (leftButtonClick)="goToPrevious()"
    (rightButtonClick)="requestNext()"
    [loading]="csvService.isLoading"
  />
}

<!-- The recommended way on (leave the problem rows out) is the main action. -->
<app-prompt-card
  title="Some rows still need fixing"
  [buttonText]="readyCount ? 'Remove them and continue with ' + formatCount(readyCount) : 'Continue with all rows'"
  tone="default"
  closeText="Keep fixing"
  [openModal]="showInvalidPrompt"
  (onClose)="closeInvalidPrompt()"
  (onButtonClick)="readyCount ? removeInvalidAndContinue() : continueAnyway()"
>
  <div class="cu__prompt">
    <p>
      {{ formatCount(invalidCount) }} of {{ formatCount(totalCount) }} rows have problems. They will be checked again and
      are likely to fail.
      @if (readyCount) {
        Leaving them out does not change your file.
      }
    </p>
    @if (readyCount) {
      <button type="button" class="bk-btn bk-btn--ghost cu__prompt-btn" (click)="continueAnyway()">
        Continue with all {{ formatCount(totalCount) }} rows
      </button>
    }
  </div>
</app-prompt-card>

<app-prompt-card
  title="Remove the rows that need fixing?"
  [buttonText]="invalidCount === 1 ? 'Remove 1 row' : 'Remove ' + formatCount(invalidCount) + ' rows'"
  tone="danger"
  [openModal]="showRemoveRowsPrompt"
  (onClose)="closeRemoveRowsPrompt()"
  (onButtonClick)="confirmRemoveRowsNeedingFixes()"
>
  <p class="cu__prompt-text">
    {{ formatCount(invalidCount) }} {{ invalidCount === 1 ? 'row is' : 'rows are' }} left out of this import, with any
    edits you made to {{ invalidCount === 1 ? 'it' : 'them' }} here. Your file is not changed.
  </p>
</app-prompt-card>
